import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Search, Download, FileSpreadsheet, FileText, File as FilePdf, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from '../../fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { getActiveSportsYear } from '../../services/sportsYearService';

interface SportsCategory {
  category_id: string;
  category_name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

interface Competition {
  event_id: string;
  event_name: string;
  gender_category: string;
  is_team_event: boolean;
  status: 'not_started' | 'in_progress' | 'completed';
  reference_number: string;
  verification_code?: string;
  competition_year: number;
  age_group?: {
    age_group_id: string;
    group_name: string;
  };
}

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const ITEMS_PER_PAGE = 15;

// Initialize pdfmake with virtual font files
pdfMake.vfs = vfs;

// Configure fonts for pdfmake
pdfMake.fonts = {
  NotoSansTamil: {
    normal: 'NotoSansTamil-Regular.ttf',
    bold: 'NotoSansTamil-Bold.ttf',
    italics: 'NotoSansTamil-Regular.ttf',
    bolditalics: 'NotoSansTamil-Bold.ttf'
  },
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Bold.ttf',
    italics: 'Roboto-Regular.ttf',
    bolditalics: 'Roboto-Bold.ttf'
  }
};

const CategoryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  const [category, setCategory] = useState<SportsCategory | null>(null);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [ageGroups, setAgeGroups] = useState<{age_group_id: string, group_name: string}[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Export menu
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCategoryData();
    }
  }, [id]);

  useEffect(() => {
    getActiveSportsYear()
      .then(setSelectedYear)
      .catch((err) => console.error('Error loading active sports year:', err));
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGender, selectedStatus, selectedAgeGroup, selectedYear, sortConfig]);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      
      // Fetch category details
      const { data: categoryData, error: categoryError } = await supabase
        .from('sports_categories')
        .select('*')
        .eq('category_id', id)
        .single();
      
      if (categoryError) throw categoryError;
      setCategory(categoryData);
      
      // Fetch competitions belonging to this category
      const { data: competitionsData, error: competitionsError } = await supabase
        .from('events')
        .select(`
          *,
          age_group:age_groups(
            age_group_id,
            group_name
          )
        `)
        .eq('category_id', id)
        .order('created_at', { ascending: false });
      
      if (competitionsError) throw competitionsError;
      setCompetitions(competitionsData || []);
      
      // Extract unique age groups for filtering
      const uniqueAgeGroups = Array.from(
        new Map(
          competitionsData
            ?.filter(comp => comp.age_group)
            .map(comp => [comp.age_group?.age_group_id, { 
              age_group_id: comp.age_group?.age_group_id, 
              group_name: comp.age_group?.group_name 
            }])
        ).values()
      );
      setAgeGroups(uniqueAgeGroups);
      
      // Extract unique years for filtering
      const uniqueYears = [...new Set(competitionsData?.map(comp => comp.competition_year))].sort((a, b) => b - a);
      setYears(uniqueYears);
      
    } catch (error: any) {
      console.error('Error fetching category data:', error);
      setError(error.message || 'Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  // Apply filters and sorting
  const getFilteredAndSortedCompetitions = () => {
    // First apply filters
    const filtered = competitions.filter(competition => {
      const matchesSearch = 
        competition.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        competition.reference_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (competition.verification_code || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesGender = selectedGender === 'All' || competition.gender_category === selectedGender;
      const matchesStatus = selectedStatus === 'All' || competition.status === selectedStatus;
      const matchesAgeGroup = selectedAgeGroup === 'All' || competition.age_group?.age_group_id === selectedAgeGroup;
      const matchesYear = competition.competition_year === selectedYear;
      
      return matchesSearch && matchesGender && matchesStatus && matchesAgeGroup && matchesYear;
    });

    // Then apply sorting if configured
    if (sortConfig !== null) {
      return [...filtered].sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);
        
        // Handle different data types
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // For numbers, dates, etc.
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }

    return filtered;
  };

  const filteredCompetitions = getFilteredAndSortedCompetitions();
  const totalPages = Math.ceil(filteredCompetitions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCompetitions = filteredCompetitions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExportToExcel = () => {
    // Format data for Excel
    const data = filteredCompetitions.map(comp => ({
      'Event Name': comp.event_name,
      'Reference Number': comp.reference_number,
      'Verification Code': comp.verification_code || '',
      'Age Group': comp.age_group?.group_name || '',
      'Gender': comp.gender_category === 'M' ? 'Male' : 
                comp.gender_category === 'F' ? 'Female' : 'All',
      'Type': comp.is_team_event ? 'Team Event' : 'Individual Event',
      'Status': comp.status === 'not_started' ? 'Not Started' :
                comp.status === 'in_progress' ? 'In Progress' : 'Completed',
      'Year': comp.competition_year
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, category?.category_name || "Category");
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${category?.category_name.replace(/\s+/g, '_')}_competitions.xlsx`);
    
    setShowExportMenu(false);
  };

  const handleExportToCSV = () => {
    // Format data for CSV
    const data = filteredCompetitions.map(comp => ({
      'Event Name': comp.event_name,
      'Reference Number': comp.reference_number,
      'Verification Code': comp.verification_code || '',
      'Age Group': comp.age_group?.group_name || '',
      'Gender': comp.gender_category === 'M' ? 'Male' : 
                comp.gender_category === 'F' ? 'Female' : 'All',
      'Type': comp.is_team_event ? 'Team Event' : 'Individual Event',
      'Status': comp.status === 'not_started' ? 'Not Started' :
                comp.status === 'in_progress' ? 'In Progress' : 'Completed',
      'Year': comp.competition_year
    }));

    const csv = Papa.unparse(data);
    const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(csvBlob, `${category?.category_name.replace(/\s+/g, '_')}_competitions.csv`);
    
    setShowExportMenu(false);
  };

  const handleExportToPDF = async () => {
    try {
      // Get school logo if available
      let logoUrl = null;
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('setting_value')
          .eq('setting_name', 'school_logo')
          .single();
        
        if (!error && data?.setting_value?.url) {
          logoUrl = data.setting_value.url;
        }
      } catch (error) {
        console.error('Error fetching school logo:', error);
      }

      let logoImage = null;
      if (logoUrl) {
        try {
          // Fetch the image and convert to base64
          const response = await fetch(logoUrl);
          const blob = await response.blob();
          const reader = new FileReader();
          logoImage = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Error loading logo image:', error);
        }
      }

      const docDefinition: TDocumentDefinitions = {
        pageOrientation: 'landscape',
        footer: function(currentPage, pageCount) {
          return {
            text: `Page ${currentPage} of ${pageCount}`,
            alignment: 'right',
            margin: [0, 0, 40, 0]
          };
        },
        content: [
          {
            columns: [
              // Left column for logo
              {
                width: 100,
                stack: logoImage ? [
                  {
                    image: logoImage as string,
                    width: 80,
                    alignment: 'left'
                  }
                ] : [],
                margin: [0, 0, 10, 0]
              },
              // Right column for title
              {
                width: '*',
                text: `${category?.category_name} - Competitions`,
                style: 'header',
                alignment: 'center'
              },
              // Empty column for balance
              {
                width: 100,
                text: ''
              }
            ]
          },
          {
            text: `Year: ${selectedYear}`,
            alignment: 'center',
            margin: [0, 5, 0, 20]
          },
          {
            table: {
              headerRows: 1,
              widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
              body: [
                [
                  { text: 'Event Name', style: 'tableHeader' },
                  { text: 'Reference Number', style: 'tableHeader' },
                  { text: 'Age Group', style: 'tableHeader' },
                  { text: 'Gender', style: 'tableHeader' },
                  { text: 'Type', style: 'tableHeader' },
                  { text: 'Status', style: 'tableHeader' },
                  { text: 'Year', style: 'tableHeader' }
                ],
                ...filteredCompetitions.map(comp => [
                  comp.event_name,
                  comp.reference_number,
                  comp.age_group?.group_name || '',
                  comp.gender_category === 'M' ? 'Male' : 
                  comp.gender_category === 'F' ? 'Female' : 'All',
                  comp.is_team_event ? 'Team Event' : 'Individual Event',
                  comp.status === 'not_started' ? 'Not Started' :
                  comp.status === 'in_progress' ? 'In Progress' : 'Completed',
                  comp.competition_year.toString()
                ])
              ]
            },
            margin: [0, 20, 0, 0]
          }
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 20]
          },
          tableHeader: {
            bold: true,
            fontSize: 12,
            color: 'black'
          }
        },
        defaultStyle: {
          font: 'Roboto'
        }
      };

      // Generate and download the PDF
      pdfMake.createPdf(docDefinition).download(`${category?.category_name.replace(/\s+/g, '_')}_competitions.pdf`);
      
      setShowExportMenu(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Category not found</div>
        <button
          onClick={() => navigate('/competitions/categories')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('categories.actions.cancel')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Layers className="h-6 w-6 mr-2 text-blue-600" />
              {category.category_name}
            </h1>
            <p className="text-sm text-gray-500">
              {category.description}
            </p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            {t('competitions.actions.export')}
          </button>
          
          {showExportMenu && (
            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1" role="menu">
                <button
                  onClick={handleExportToExcel}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {t('participants.export.toExcel')}
                </button>
                <button
                  onClick={handleExportToCSV}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('participants.export.toCSV')}
                </button>
                <button
                  onClick={handleExportToPDF}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <FilePdf className="h-4 w-4 mr-2" />
                  {t('participants.export.toPDF')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('competitions.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allGenders')}</option>
                <option value="M">{t('competitions.filters.male')}</option>
                <option value="F">{t('competitions.filters.female')}</option>
                <option value="A">{t('competitions.filters.all')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedAgeGroup}
                onChange={(e) => setSelectedAgeGroup(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allAgeGroups')}</option>
                {ageGroups.map(group => (
                  <option key={group.age_group_id} value={group.age_group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allStatuses')}</option>
                <option value="not_started">{t('competitions.status.not_started')}</option>
                <option value="in_progress">{t('competitions.status.in_progress')}</option>
                <option value="completed">{t('competitions.status.completed')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {filteredCompetitions.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('competitions.noCompetitions.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedGender !== 'All' || selectedStatus !== 'All' || selectedAgeGroup !== 'All'
                ? t('competitions.noCompetitions.description.withFilters')
                : t('competitions.noCompetitions.description.noFilters')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('event_name')}
                    >
                      {t('competitions.table.eventName')}
                      {getSortIcon('event_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('reference_number')}
                    >
                      {t('competitions.table.referenceNumber')}
                      {getSortIcon('reference_number')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('age_group.group_name')}
                    >
                      {t('competitions.table.ageGroup')}
                      {getSortIcon('age_group.group_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('gender_category')}
                    >
                      {t('competitions.table.gender')}
                      {getSortIcon('gender_category')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('is_team_event')}
                    >
                      Type
                      {getSortIcon('is_team_event')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('status')}
                    >
                      {t('competitions.table.status')}
                      {getSortIcon('status')}
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">{t('competitions.table.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCompetitions.map((competition) => (
                    <tr key={competition.event_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {competition.event_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {competition.reference_number}
                        </div>
                        {competition.verification_code && (
                          <div className="text-xs font-mono text-gray-500">
                            {competition.verification_code}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {competition.age_group?.group_name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.gender_category === 'M' ? 'bg-blue-100 text-blue-800' :
                          competition.gender_category === 'F' ? 'bg-pink-100 text-pink-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {competition.gender_category === 'M' ? t('competitions.filters.male') :
                           competition.gender_category === 'F' ? t('competitions.filters.female') : 
                           t('competitions.filters.all')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.is_team_event ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {competition.is_team_event ? 'Team Event' : 'Individual Event'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                          competition.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {t(`competitions.status.${competition.status}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/competitions/${competition.event_id}`} className="text-blue-600 hover:text-blue-800">
                          {t('competitions.actions.view')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(startIndex + ITEMS_PER_PAGE, filteredCompetitions.length)}
                      </span>{' '}
                      of <span className="font-medium">{filteredCompetitions.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryDetails;